import CustomModal from 'components/Common/CustomModal'
import React, { useRef, useState, useMemo } from 'react'
import { inputStyle } from './CreateOrder'
import { ReactComponent as Upload } from "../../assets/img/uploadfile.svg"
import { getMediaUrl } from 'config'
import api from 'api'
import { toast } from 'react-toastify'

const labelStyle = {
    marginTop: '10px',
    fontWeight: 600,
    fontSize: '16px',
    fontFamily: "Outfit",
    color: '#1C1C1C'
};

const hintStyle = {
    color: '#828282',
    fontSize: '13px',
    fontWeight: 400,
    fontFamily: "Outfit",
    display: 'block',
    marginTop: '4px',
};

const selectStyle = {
    backgroundColor: '#F3F3F3',
    border: 'none',
    borderRadius: '8px',
    fontFamily: "Outfit",
    width: "100%",
    padding: '12px 17px',
    fontSize: '14px',
    color: '#1C1C1C',
    cursor: 'pointer',
    appearance: 'auto',
};

const AddBatch = ({ handleModal, selectedOrder, getOrders, allOrders }) => {
    const [file, setFile] = useState();
    const [loading, setLoading] = useState(false);
    const [existingImage, setExistingImage] = useState(null);
    const uploadref = useRef(null)
    const [data, setData] = useState({
        name: '',
        description: '',
    })

    // Build a deduplicated list of previous products from the same brand
    // across all loaded orders. Most-recent first.
    const previousProducts = useMemo(() => {
        if (!allOrders || !selectedOrder?.brandId?._id) return [];
        const brandId = selectedOrder.brandId._id;
        const seen = new Set();
        const products = [];

        // Iterate orders (newest first since getOrders sorts by createdAt desc)
        for (const order of allOrders) {
            if (String(order?.brandId?._id) !== String(brandId)) continue;
            for (const batch of (order.batchIds || [])) {
                const name = batch?.product_name;
                if (!name || seen.has(name)) continue;
                seen.add(name);
                products.push({
                    name,
                    description: batch?.text || '',
                    image: batch?.product_img || null,
                    coaLink: batch?.coaLink || '',
                });
            }
        }
        return products;
    }, [allOrders, selectedOrder]);

    const handleCopyFrom = (e) => {
        const productName = e.target.value;
        if (!productName) {
            // "New product" selected — clear
            setData({ name: '', description: '' });
            setExistingImage(null);
            setFile(undefined);
            return;
        }
        const prev = previousProducts.find((p) => p.name === productName);
        if (prev) {
            setData({ name: prev.name, description: prev.description });
            setExistingImage(prev.image);
            setFile(undefined); // Clear any manually-selected file; user can re-upload if wanted
        }
    };

    const handleAddBatch = () => {
        setLoading(true)
        const formdata = new FormData()
        formdata.append('product_name', data.name)
        formdata.append('text', data.description)
        formdata.append('brand', selectedOrder?.brandId._id)

        if (file) {
            // User selected a new file — upload it
            formdata.append('product_img', file)
        } else if (existingImage) {
            // Copying image URL from previous product (no re-upload needed)
            formdata.append('product_img', existingImage)
        }

        api('post', `/requests/orderBatch/${selectedOrder._id}`, formdata).then((res) => {
            setLoading(false)
            toast.success(res.message)
            handleModal()
            getOrders()
        }).catch((err) => {
            setLoading(false)
            console.log(err)
        })
    }

    const handleChange = (e) => {
        setData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
    }

    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    const handleFileChange = (event) => {
        if (event.target.files.length) {
            const selectedFile = event.target.files[0];
            if (selectedFile.size > MAX_FILE_SIZE) {
                toast.error('File size must be less than 5MB');
                event.target.value = '';
                setFile();
                return;
            }
            setFile(selectedFile);
            setExistingImage(null); // Override copied image with new upload
        } else {
            setFile();
        }
    };

    // Resolve image preview: user-selected file > copied image URL > nothing
    const imagePreview = file
        ? URL.createObjectURL(file)
        : existingImage
            ? getMediaUrl(existingImage)
            : null;

    return (
        <CustomModal handleSave={handleAddBatch} heading='Add Batch' buttonName='Add' handleModal={handleModal} loading={loading}>
            {/* ── Copy from previous product ── */}
            {previousProducts.length > 0 && (
                <>
                    <label style={labelStyle}>Copy from previous product</label><br />
                    <select style={selectStyle} onChange={handleCopyFrom} defaultValue="">
                        <option value="">New product (blank)</option>
                        {previousProducts.map((p) => (
                            <option key={p.name} value={p.name}>{p.name}</option>
                        ))}
                    </select>
                    <span style={hintStyle}>Pre-fills name, description, and image. Edit anything after selecting.</span>
                </>
            )}

            {/* ── Product Image ── */}
            <input
                type="file"
                id="fileUpload"
                onChange={handleFileChange}
                style={{ display: "none" }}
                ref={uploadref}
                accept="image/*"
            />
            <label style={labelStyle}>Product Image</label><br />
            {!imagePreview ?
                <label className='d-flex justify-content-center align-items-center' htmlFor="fileUpload" style={{
                    backgroundColor: '#F3F3F3',
                    border: '2px dashed #E4E4E4',
                    borderRadius: '16px',
                    fontFamily: "Outfit",
                    width: "100%",
                    height: "120px",
                    cursor: "pointer"
                }}>
                    <div className='d-flex flex-column justify-content-center align-items-center'>
                        <Upload />
                        <span className='mt-1'>Upload Image</span>
                    </div>
                </label>
                :
                <div style={{ height: "120px", cursor: "pointer", position: "relative" }}
                    onClick={() => { uploadref.current.click() }}
                    className='d-flex justify-content-center'>
                    <img style={{ objectFit: "cover", maxHeight: "120px", borderRadius: "8px" }} src={imagePreview} alt="" />
                    {existingImage && !file && (
                        <span style={{
                            position: 'absolute', bottom: 4, right: 4,
                            background: 'rgba(0,0,0,0.6)', color: '#fff',
                            fontSize: 11, padding: '2px 8px', borderRadius: 4,
                        }}>Click to replace</span>
                    )}
                </div>
            }

            {/* ── Product Name ── */}
            <label style={labelStyle}>Product Name</label><br />
            <div className='text-right'>
                <input onChange={handleChange} name='name' value={data.name} placeholder='Enter product name' style={inputStyle} type="text" maxLength={24} />
                <span style={{ ...hintStyle, textAlign: 'right' }}>{data.name.length}/24</span>
            </div>

            {/* ── Product Description ── */}
            <label style={labelStyle}>Product Description</label><br />
            <div className='text-right'>
                <textarea onChange={handleChange} name='description' value={data.description} placeholder='Describe the product batch' maxLength={120} style={{
                    backgroundColor: '#F3F3F3',
                    border: 'none',
                    borderRadius: '8px',
                    fontFamily: "Outfit",
                    width: "100%",
                    padding: '12px 17px',
                    resize: "none"
                }} />
                <span style={{ ...hintStyle, textAlign: 'right' }}>{data.description.length}/120</span>
            </div>
        </CustomModal>
    )
}

export default AddBatch
